// Report a bug — every screen, every role (Law 14). The top bar's ? menu opens it as its last item (UAT plan D-2, SH-5:
// `top.bug`), and the flow is the design system's Dialog: glass over the scrim, the note in a Field, the red Send
// blocked until the note has three characters. Context is allow-listed and previewed before it is sent; a lost
// response is retried with the same idempotency key, so a report is never sent twice. The report's state lives here,
// so one still sending or failed survives closing the dialog and opening it again. The preview reads in words (UAT
// F-76): each known key by its name ("Screen: Home"), an empty one left out, no underscore in a name; the payload's
// own JSON waits under Technical details, and the D0 Dialog keeps Send and Cancel in view over the scrim.
import { useRef, useState, type ReactNode } from "react";
import { AppError, newIdempotencyKey } from "@/api/runtime";
import { feedbackReport, type FeedbackReportOutput, type FeedbackReportInput } from "@/api/tools";
import { useToast } from "@/shell/Toasts";
import { useViewState } from "@/shell/viewState";
import { Banner, Button, Disclosure, Field, Paragraph, TextArea, type Blocked } from "@/mc/core";
import { Dialog } from "@/mc/g4b";
import { useIsoLayoutEffect } from "@/mc/lib";

type Phase = "open" | "sending" | "failed" | "sent";

/** What a report carries besides the note: the route and the allow-listed context. */
export type BugPayload = Omit<FeedbackReportInput, "note">;

/** The words for the keys a report's context carries; the payload keeps its keys. */
const CONTEXT_WORDS: Readonly<Record<string, string>> = {
  route: "Screen", view: "View", ids: "Records", state_summary: "Screen state", client: "Browser", viewport: "Window size", build: "Build",
};
/** The words for a screen's coded state (viewState's summary). */
const STATE_WORDS: Readonly<Record<string, string>> = { state: "State", status: "Status", stage: "Stage", tab: "Tab", count: "Count" };

/** A key or a coded value as words: underscores read as spaces ("state_summary" → "state summary"). */
const spaced = (text: string) => text.replace(/_/g, " ");
const sentence = (text: string) => spaced(text).replace(/^./, (first) => first.toUpperCase());

/** A context value as words, never JSON: empty values give "". */
function valueWords(key: string, value: unknown): string {
  if (value === null || value === undefined || value === false) return "";
  if (typeof value !== "object") {
    const text = String(value);
    return key === "view" ? spaced(text) : key === "viewport" ? text.replace(/^(\d+)x(\d+)$/, "$1 × $2") : text;
  }
  if (Array.isArray(value)) return value.map((item) => valueWords("", item)).filter(Boolean).join(", ");
  const entries = Object.entries(value as Record<string, unknown>);
  if (key === "ids") return entries.map(([, id]) => valueWords("", id)).filter(Boolean).join(" · ");
  return entries.map(([name, inner]) => {
    if (inner === true) return sentence(name);
    const words = valueWords("", inner);
    return words ? `${STATE_WORDS[name] ?? sentence(name)} ${typeof inner === "string" ? spaced(words) : words}` : "";
  }).filter(Boolean).join(" · ");
}

export interface BugContextRow {
  /** The payload key the row shows and Remove takes out. */
  key: string;
  label: string;
  value: string;
}

/** The rows the preview shows (UAT F-76): the route as the screen's name, each context key in words, empty ones left out. */
export function contextRows(payload: BugPayload, screen?: string): BugContextRow[] {
  const entries: [string, unknown][] = [["route", payload.route], ...Object.entries(payload.context ?? {})];
  return entries.flatMap(([key, value]) => {
    const words = key === "route" && value ? screen || String(value) : valueWords(key, value);
    return words ? [{ key, label: CONTEXT_WORDS[key] ?? sentence(key), value: words }] : [];
  });
}

/** The preview: each row with its Remove, then the payload's JSON under Technical details (with `technical` after it). */
export function BugContext({ payload, screen, held, onRemove, technical }: {
  payload: BugPayload;
  screen?: string | undefined;
  held?: Blocked | undefined;
  onRemove: (key: string) => void;
  technical?: ReactNode;
}) {
  return (
    <>
      <dl className="mc-bug-context">
        {contextRows(payload, screen).map((row) => (
          <div key={row.key}>
            <dt>{row.label}</dt>
            <dd>
              <span>{row.value}</span>
              <Button sm tone="ghost" blocked={held} onClick={() => onRemove(row.key)} aria-label={`Remove ${row.label.toLowerCase()}`}>Remove</Button>
            </dd>
          </div>
        ))}
      </dl>
      <Disclosure title="Technical details" sm >
        <pre className="mc-doc-text mc-mono">{JSON.stringify(payload, null, 2)}</pre>
        {technical}
      </Disclosure>
    </>
  );
}

export interface BugReportProps {
  open: boolean;
  onClose: () => void;
  /** The screen's name in words (the shell's crumbs), shown for the route. */
  screen?: string | undefined;
}

export function BugReport({ open, onClose, screen }: BugReportProps) {
  const isOpen = useRef(open);
  isOpen.current = open;
  const [touched, setTouched] = useState(false);
  const [phase, setPhase] = useState<Phase>("open");
  const [note, setNote] = useState("");
  const [captured, setCaptured] = useState<BugPayload>({ route: "", context: {} });
  const [screenWords, setScreenWords] = useState("");
  const [removed, setRemoved] = useState<string[]>([]);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [receipt, setReceipt] = useState<FeedbackReportOutput | null>(null);
  const attempt = useRef<{ key: string; report: FeedbackReportInput } | null>(null);
  const sending = useRef(false);
  const { snapshot } = useViewState();
  const { toast } = useToast();
  const frozen = phase !== "open";
  const report: FeedbackReportInput = attempt.current?.report ?? {
    note: note.trim(), route: removed.includes("route") ? "" : captured.route,
    context: Object.fromEntries(Object.entries(captured.context ?? {}).filter(([key]) => !removed.includes(key))),
  };

  // Opening reads the screen afresh, before the dialog paints, unless a report is still sending or failed: its note
  // and context are kept for Retry.
  const phaseNow = useRef(phase);
  phaseNow.current = phase;
  useIsoLayoutEffect(() => {
    if (!open || (phaseNow.current !== "open" && phaseNow.current !== "sent")) return;
    const view = snapshot();
    // Mirror the server diagnostic projection so the preview is the actual retained payload.
    // Narrative view summaries are deliberately excluded: the server accepts coded state only.
    const ids = Object.fromEntries(Object.entries(view.ids).filter(([key, value]) => /^[a-z_]+$/.test(key) && /^(?:R-\d+|RQ?-\d+|[a-z]{1,12}_[a-zA-Z0-9_-]+)$/.test(value)));
    const summary = typeof view.summary === "string" ? {} : view.summary;
    const path = (window.location.hash || "#today").split("?")[0]!;
    setScreenWords(screen ?? "");
    setCaptured({ route: /^[#A-Za-z0-9/_.:-]{0,200}$/.test(path) ? path : "", context: {
      view: view.view, ids, state_summary: summary,
      client: ["Firefox", "Chrome", "Safari"].find(browser => navigator.userAgent.includes(browser)) ?? "other",
      viewport: `${window.innerWidth}x${window.innerHeight}`, build: import.meta.env.VITE_BUILD ?? "dev",
    } });
    setPhase("open"); setNote(""); setRemoved([]); setError(null); setConflict(false); setReceipt(null); setCopied(false);
    setTouched(false);
    attempt.current = null;
  }, [open]);

  function cancel() {
    onClose();
    if (!sending.current) { setPhase("open"); attempt.current = null; }
  }
  async function send() {
    if (sending.current || phase === "sent" || conflict || note.trim().length < 3) return;
    sending.current = true;
    attempt.current ??= { key: newIdempotencyKey(), report: structuredClone(report) };
    setPhase("sending"); setError(null);
    try {
      const result = await feedbackReport(attempt.current.report, attempt.current.key);
      setReceipt(result); setPhase("sent");
      toast(`Reported · ${result.public_ref} · we have it`);
    } catch (e) {
      const differentInput = e instanceof AppError && e.code === "IDEMPOTENCY_CONFLICT";
      setConflict(differentInput);
      setError(differentInput ? "We could not confirm this report because its saved request does not match. Copy the report for support."
        : e instanceof AppError && e.status === 409 ? "The report is still awaiting confirmation. Retry to check the same report; it will not send a second copy."
        : e instanceof AppError ? `${e.message}${e.remedy ? " — " + e.remedy : ""}` : "Could not send the report.");
      setPhase("failed");
      if (!isOpen.current) toast("Failed to send the bug report. Open Report a bug to retry; your note is saved.", { bad: true });
    } finally { sending.current = false; }
  }
  const hint = phase === "sending" ? "Sending your report. You may close this dialog; sending will continue."
    : frozen ? "Your submitted note and context are kept unchanged so Retry can recover the original receipt."
    : "Describe what went wrong in at least three characters to send this report.";
  const short = note.trim().length < 3;
  const held = { reason: hint, describedBy: "bug-send-hint" };

  return (
    <Dialog
      title="Report a bug"
      open={open}
      // Escape and the close button hide the dialog and keep the report; Cancel and Done end it, unless it is sending.
      onClose={(reason) => (reason === "dismiss" && phase !== "sending" ? cancel() : onClose())}
      width={560}
      primary={phase === "sent" ? undefined : {
        label: phase === "failed" ? "Retry" : "Send",
        onClick: () => void send(),
        busy: phase === "sending",
        blocked: conflict ? { reason: error ?? hint, describedBy: "bug-send-hint" } : short ? held : undefined,
      }}
      dismissLabel={phase === "sent" ? "Done" : phase === "sending" ? "Close" : "Cancel"}
    >
      <div id="bug-panel">
        <Paragraph>This is what will be sent with your note: the screen you are on and its ids. Never a screenshot, never candidate details.</Paragraph>
        <BugContext
          payload={{ route: report.route, context: report.context ?? {} }}
          screen={screenWords}
          held={frozen ? held : undefined}
          onRemove={(key) => setRemoved(previous => [...previous, key])}
          technical={phase === "sent" ? <Paragraph sm>Report {receipt?.id} · Receipt #{receipt?.receipt_id}</Paragraph> : null}
        />
        <p className="mc-p mc-p-sm" id="bug-send-hint" role="status">{phase === "sent" ? "Your report was received." : hint}</p>
        <span className="mc-sr" role="status">{copied ? "Report copied." : ""}</span>
        {phase === "sent" ? (
          <Banner tone="ok">Reported · <span className="mc-mono">{receipt?.public_ref}</span> · we have it.</Banner>
        ) : (
          <>
            <Field id="bug-note" label="What went wrong, and what you expected" required hint="Use at least three characters, up to 2000." error={touched && short ? "Enter at least three characters." : undefined}>
              <TextArea value={note} onChange={event => setNote(event.target.value)} onBlur={() => setTouched(true)} minLength={3} maxLength={2000} blocked={frozen ? held : undefined} />
            </Field>
            {error ? (
              <Banner tone="red" live right={
                <Button sm onClick={async () => {
                  try { await navigator.clipboard.writeText(JSON.stringify(report, null, 2)); setCopied(true); }
                  catch { setError("Copy was unavailable. Your report is still here."); }
                }}>Copy the report</Button>
              }>{error}</Banner>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
}
